import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChartNoAxesCombined,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Plus,
  RefreshCw,
  Search,
  ExternalLink,
  Pencil,
} from "lucide-react";
import { apiFetch } from "../../commons/axiosReq";
import { siteUrl } from "../../config/urls";
import { csvCell } from "../../components/MainAdmin/Articles/articleContent";
import "../../components/MainAdmin/Articles/Articles.css";

const statuses = {
  published: "منتشرشده",
  scheduled: "زمان‌بندی‌شده",
  pending: "در انتظار تأیید",
  rejected: "ردشده",
  unknown: "نامشخص",
};
const initialFilters = {
  search: "",
  status: "",
  category: "",
  userId: "",
  from: "",
  to: "",
  sort: "modified",
  needsReview: false,
};
const number = (value) => Number(value || 0).toLocaleString("fa-IR");
const date = (value) =>
  value && !Number.isNaN(new Date(value).getTime())
    ? new Date(value).toLocaleDateString("fa-IR")
    : "—";
const queryString = (filters, page, pageSize) =>
  new URLSearchParams(
    Object.entries({ ...filters, page, pageSize }).filter(
      ([, value]) => value !== "",
    ),
  ).toString();

export default function MainAdminArticleReport() {
  const [filters, setFilters] = useState(initialFilters);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [result, setResult] = useState(null);
  const [options, setOptions] = useState({ categories: [], authors: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [optionError, setOptionError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [exportProgress, setExportProgress] = useState(0);
  const [updated, setUpdated] = useState(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((current) => ({ ...current, search }));
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    let active = true;
    apiFetch("Article/GetEditorOptions").then((response) => {
      if (!active) return;
      if (response.ok) {
        setOptions(response.data);
        setOptionError("");
      } else
        setOptionError(
          "فهرست دسته‌بندی و نویسندگان دریافت نشد؛ برای تلاش دوباره از تازه‌سازی استفاده کنید.",
        );
    });
    return () => {
      active = false;
    };
  }, [refresh]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    if (filters.from && filters.to && filters.from > filters.to) {
      setError("تاریخ شروع باید قبل از تاریخ پایان باشد.");
      setLoading(false);
      return;
    }
    apiFetch(
      `Article/GetArticleReport?${queryString(filters, page, pageSize)}`,
    ).then((response) => {
      if (!active) return;
      if (response.ok && Array.isArray(response.data?.data)) {
        setResult(response.data);
        setUpdated(new Date());
      } else {
        setResult(null);
        setError(response.error || "گزارش دریافت نشد.");
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [filters, page, pageSize, refresh]);
  const change = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };
  const exportCsv = async () => {
    setExporting(true);
    setExportError("");
    setExportProgress(0);
    try {
      const rows = [];
      let total = 1;
      for (let current = 1; rows.length < total; current++) {
        const response = await apiFetch(
          `Article/GetArticleReport?${queryString(filters, current, 100)}`,
        );
        if (!response.ok || !Array.isArray(response.data?.data))
          throw new Error(response.error || "دریافت خروجی ناموفق بود.");
        total = response.data.count;
        if (!response.data.data.length && rows.length < total)
          throw new Error("فهرست هنگام دریافت تغییر کرد؛ دوباره تلاش کنید.");
        rows.push(...response.data.data);
        setExportProgress(rows.length);
      }
      const headings = [
        "شناسه",
        "عنوان",
        "نامک",
        "وضعیت",
        "نویسنده",
        "دسته‌بندی",
        "برچسب‌ها (تعداد)",
        "بازدید",
        "تاریخ انتشار",
        "آخرین ویرایش",
        "موارد ناقص",
      ];
      const csv = [
        headings,
        ...rows.map((item) => [
          item.articleId,
          item.articleTittle,
          item.slug,
          statuses[item.status],
          item.authorName,
          item.categoryNames.join("، "),
          item.tagCount,
          item.views,
          item.datePublished,
          item.dateModified,
          item.issues.join("، "),
        ]),
      ]
        .map((row) => row.map(csvCell).join(","))
        .join("\r\n");
      const url = URL.createObjectURL(
        new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `articles-report-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setExportError(error.message);
    } finally {
      setExporting(false);
    }
  };
  const summary = result?.summary;
  const pages = Math.max(1, Math.ceil((result?.count || 0) / pageSize));
  const currentPage = result?.page || page;
  return (
    <div className="article-workspace article-report" dir="rtl">
      <header className="article-header">
        <div>
          <span className="article-eyebrow">مجله اینفینیتی • تحلیل محتوا</span>
          <h1>گزارش مقالات</h1>
          <p>انتشار، بازدید و کامل‌بودن اطلاعات همه مقاله‌ها در یک نگاه.</p>
        </div>
        <Link className="article-primary" to="/mainAdmin/uploadNews/release">
          <Plus size={18} />
          نوشتن مقاله
        </Link>
      </header>
      <div className="article-report-intro">
        <span>
          <ChartNoAxesCombined size={18} />
          آمار بر اساس فیلترهای انتخاب‌شده
        </span>
        <small>
          {updated
            ? `آخرین دریافت: ${updated.toLocaleTimeString("fa-IR")}`
            : "در انتظار دریافت اطلاعات"}
        </small>
      </div>
      <div className="article-metrics">
        {[
          ["total", "کل مقالات", FileText],
          ["published", "منتشرشده", Check],
          ["views", "مجموع بازدید ثبت‌شده", Eye],
          ["needsReview", "اطلاعات ناقص", ChartNoAxesCombined],
        ].map(([key, label, Icon]) => (
          <div key={key}>
            <span>
              <Icon size={20} />
            </span>
            <p>{label}</p>
            <strong>
              {loading || !summary || error ? "—" : number(summary[key])}
            </strong>
          </div>
        ))}
      </div>
      <section className="article-panel article-report-controls">
        <div className="article-filter-top">
          <label className="article-report-search">
            <Search size={18} />
            <input
              aria-label="جستجوی مقالات"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="جستجوی عنوان، نامک یا نویسنده…"
            />
          </label>
          <button
            disabled={loading || exporting}
            onClick={() => setRefresh((value) => value + 1)}
          >
            <RefreshCw size={16} />
            تازه‌سازی
          </button>
          <button
            disabled={loading || exporting || !result?.count || Boolean(error)}
            onClick={exportCsv}
          >
            <Download size={16} />
            {exporting
              ? `دریافت ${number(exportProgress)} مقاله…`
              : "خروجی CSV همه نتایج"}
          </button>
        </div>
        <fieldset disabled={exporting} className="article-filters">
          <label>
            وضعیت
            <select
              value={filters.status}
              onChange={(event) => change("status", event.target.value)}
            >
              <option value="">همه وضعیت‌ها</option>
              {Object.entries(statuses)
                .filter(([key]) => key !== "unknown")
                .map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
            </select>
          </label>
          <label>
            دسته‌بندی
            <select
              value={filters.category}
              onChange={(event) => change("category", event.target.value)}
            >
              <option value="">همه دسته‌بندی‌ها</option>
              {options.categories?.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            نویسنده
            <select
              value={filters.userId}
              onChange={(event) => change("userId", event.target.value)}
            >
              <option value="">همه نویسندگان مجاز</option>
              {options.authors?.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            انتشار از
            <input
              type="date"
              value={filters.from}
              onChange={(event) => change("from", event.target.value)}
            />
          </label>
          <label>
            انتشار تا
            <input
              type="date"
              value={filters.to}
              onChange={(event) => change("to", event.target.value)}
            />
          </label>
          <label>
            ترتیب
            <select
              value={filters.sort}
              onChange={(event) => change("sort", event.target.value)}
            >
              <option value="modified">آخرین ویرایش</option>
              <option value="views">بیشترین بازدید</option>
              <option value="oldest">قدیمی‌ترین انتشار</option>
            </select>
          </label>
        </fieldset>
        <div className="article-filter-bottom">
          <label className="article-checkbox">
            <input
              type="checkbox"
              checked={filters.needsReview}
              disabled={exporting}
              onChange={(event) => change("needsReview", event.target.checked)}
            />
            فقط مقاله‌های با اطلاعات ناقص
          </label>
          <button
            className="article-text-button"
            disabled={exporting}
            onClick={() => {
              setFilters(initialFilters);
              setSearch("");
              setPage(1);
            }}
          >
            پاک‌کردن فیلترها
          </button>
          <small>
            ناقص: خلاصه، تصویر، نامک، دسته‌بندی یا برچسب ثبت نشده است.
          </small>
        </div>
      </section>
      {optionError && (
        <p className="article-notice" role="status">
          {optionError}
        </p>
      )}
      {exportError && (
        <p className="article-notice is-error" role="alert">
          {exportError}
        </p>
      )}
      <div className="article-report-statuses">
        <span>
          منتشرشده{" "}
          <b>
            {summary && !loading && !error ? number(summary.published) : "—"}
          </b>
        </span>
        <span>
          زمان‌بندی‌شده{" "}
          <b>
            {summary && !loading && !error ? number(summary.scheduled) : "—"}
          </b>
        </span>
        <span>
          در انتظار تأیید{" "}
          <b>{summary && !loading && !error ? number(summary.pending) : "—"}</b>
        </span>
        <span>
          ردشده{" "}
          <b>
            {summary && !loading && !error ? number(summary.rejected) : "—"}
          </b>
        </span>
      </div>
      {loading ? (
        <div className="article-state" role="status">
          در حال دریافت گزارش…
        </div>
      ) : error ? (
        <div className="article-state" role="alert">
          <h2>گزارش دریافت نشد</h2>
          <p>{error}</p>
          <button onClick={() => setRefresh((value) => value + 1)}>
            تلاش دوباره
          </button>
        </div>
      ) : !result?.data.length ? (
        <div className="article-state">
          <Search size={30} />
          <h2>مقاله‌ای پیدا نشد</h2>
          <p>فیلترها را تغییر دهید یا یک مقاله جدید بنویسید.</p>
        </div>
      ) : (
        <>
          <div className="article-report-table-wrap">
            <table className="article-report-table">
              <thead>
                <tr>
                  <th>مقاله</th>
                  <th>وضعیت</th>
                  <th>نویسنده و دسته‌بندی</th>
                  <th>بازدید</th>
                  <th>تاریخ‌ها</th>
                  <th>کامل‌بودن اطلاعات</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {result.data.map((item) => (
                  <tr key={item.articleId}>
                    <td>
                      <strong>{item.articleTittle}</strong>
                      <small className="article-report-slug" dir="auto">
                        {item.slug}
                      </small>
                    </td>
                    <td>
                      <span className={`article-status ${item.status}`}>
                        {statuses[item.status] || statuses.unknown}
                      </span>
                    </td>
                    <td>
                      {item.authorName || "ثبت نشده"}
                      <small>
                        {item.categoryNames.join("، ") || "بدون دسته‌بندی"}
                      </small>
                    </td>
                    <td>
                      <b>{number(item.views)}</b>
                    </td>
                    <td>
                      <span>انتشار: {date(item.datePublished)}</span>
                      <small>ویرایش: {date(item.dateModified)}</small>
                    </td>
                    <td>
                      {item.issues.length ? (
                        <span className="article-issues">
                          {item.issues.join(" · ")}
                        </span>
                      ) : (
                        <span className="article-complete">
                          <Check size={14} />
                          اطلاعات پایه کامل
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="article-row-actions">
                        <Link
                          aria-label={`ویرایش ${item.articleTittle}`}
                          to={`/mainAdmin/editNews/release?slug=${encodeURIComponent(item.slug)}`}
                        >
                          <Pencil size={16} />
                          ویرایش
                        </Link>
                        {item.status === "published" && (
                          <a
                            aria-label={`مشاهده ${item.articleTittle}`}
                            href={siteUrl(
                              `/blog-details/${encodeURIComponent(item.slug)}`,
                            )}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <ExternalLink size={16} />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <footer className="article-pagination">
            <span>
              {number(result.count)} مقاله · صفحه {number(currentPage)} از{" "}
              {number(pages)}
            </span>
            <label>
              تعداد در صفحه
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>۱۰</option>
                <option value={20}>۲۰</option>
                <option value={50}>۵۰</option>
              </select>
            </label>
            <div>
              <button
                aria-label="صفحه قبل"
                disabled={currentPage <= 1}
                onClick={() => setPage(currentPage - 1)}
              >
                <ChevronRight size={18} />
              </button>
              <b>{number(currentPage)}</b>
              <button
                aria-label="صفحه بعد"
                disabled={currentPage >= pages}
                onClick={() => setPage(currentPage + 1)}
              >
                <ChevronLeft size={18} />
              </button>
            </div>
          </footer>
        </>
      )}
      <p className="article-report-footnote">
        بازدیدها شمارنده ثبت‌شده سامانه‌اند و آمار Search Console نیستند. بررسی
        ساختار تیترها و تصاویر داخل متن در صفحه ویرایش هر مقاله در دسترس است.
      </p>
    </div>
  );
}
